/**
 * File: apps/qbench-cp/reactapp/src/shared/components/Search/Search.jsx
 *
 * Purpose: Renders the dashboard's main search input, driving the shared search filter and
 *     redirecting to the orders list to show results.
 *
 * Scope: Shared Search component used on the customer portal dashboard.
 *
 * Tags: customer-portal
 *
 * Overview: Keeps a local search string in sync with the DashboardContext's mainSearch filter,
 *     dispatching updateMainSearch and navigating to /portal/orders when the user presses Enter
 *     or clicks the search icon, and re-running the search when the field is cleared while a
 *     search was previously active. The placeholder text is generated dynamically from order
 *     and sample entity display names plus up to two configured sample field display names
 *     pulled from field configuration. A clear icon resets the field and notifies the optional
 *     onChange prop as the user types.
 *
 * Exports: Search (default export).
 */

import closeIcon from 'assets/images/svg/close-small.svg';
import searchIcon from 'assets/images/svg/search.svg';
import { useFieldConfig } from 'context/fields';
import { updateMainSearch } from 'Dashboard/reducers/filters';
import { DashboardContext } from 'Dashboard/state';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from 'shared/components/common/Input';
import { EntityType } from 'shared/constants/entityTypes';
import { useEntityDisplayName } from 'shared/hooks/misc/fields';
import styled from 'styled-components';

const StyledSearch = styled.div`
	height: 32px;
	background-color: ${({theme}) => theme.color.palette.background.paper.defaultLight};
`

const Search = ({onChange=()=>{}, ...props}) => {
	const [orderSingular] = useEntityDisplayName(EntityType.ORDER);
    const [sampleSingular] = useEntityDisplayName(EntityType.SAMPLE);
		const { getFields } = useFieldConfig();
		const fields = getFields({ entityType: EntityType.SAMPLE, useGlobal: true });
	
	const {
		state: {
			filters: { mainSearch },
		},
		dispatch,
	} = React.useContext(DashboardContext);
	const { pathname } = useLocation();
	const navigate = useNavigate();

    const [search, setSearch] = React.useState(mainSearch || '');
	React.useEffect(() => {
        if(typeof value === 'string') {
            setSearch(mainSearch)
        }
    }, [mainSearch])
	
	React.useEffect(() => {
        if(search === '' && mainSearch) {
			onEnter();
        }
    }, [search])

	const redirectToResultPage = React.useCallback(() => {
		const path = '/portal/orders';
		search && pathname !== path && navigate(path);
	}, [search, pathname, navigate]);

	const onEnter = React.useCallback(() => {
			dispatch(updateMainSearch(search));
			redirectToResultPage();
		},
		[dispatch, search, redirectToResultPage]
	);

	const handleOnChange = React.useCallback(
		e => {
			const value = e.target.value;
            setSearch(value);
            onChange(value);
		},
		[onChange]
	);

    const handleOnEnter = React.useCallback(
		(e) => {
			if (e.type === 'keydown' && e.key === 'Enter') {
				onEnter();
			} else if (e.type === 'click' && e.currentTarget.id === 'inputSubmit') {
				onEnter();
			}
		},
		[onEnter]
	);

	const onClearIconClick = React.useCallback(() => {
		if (search.length) {
			setSearch('');
			onChange('');			
		}
	}, [onChange]);

	const generatePlaceholder = () => {
		const baseCopy = `Search ${orderSingular} ID, ${sampleSingular} ID,`;
		const itemsToShow = Math.min(fields.length, 2);
		const separator = itemsToShow ? ' ' : '';
		const extras = fields.map(field => field.displayName || '').slice(0, itemsToShow).join(', ');
		return props.placeholder || `${baseCopy}${separator}${extras} or others`;
	}

	return (
		<StyledSearch className='searchWrapper'>
			<Input
				type='text'
				value={search}
				onChange={handleOnChange}
				onEnter={handleOnEnter}
				onIconClick={handleOnEnter}
				onClearIconClick={onClearIconClick}
				placeholder={generatePlaceholder()}
				icon={searchIcon}
				clearIcon={closeIcon}
				{...props}
			/>
		</StyledSearch>
	);
};

export default Search;